/* ─────────────────────────────────────────────
   PER-ROUTE SEO CONFIGURATION
   routeMeta[logicalRoute][lang] = { title, description, h1, type }

   - Keyed by the LOGICAL route (no /en, /de prefix).
   - All three languages (sr / en / de) are fully populated; getMeta() still
     falls back to Serbian field-by-field if a key is ever missing.
   - `type` is the Open Graph og:type ('website' everywhere, 'article'
     only for the /balayage blog post).

   DELIBERATE COPY DECISIONS - do not "fix" these:
   1. The city is spelled "Nis" (no diacritic) throughout EN and DE. German
      and English keyboards have no "š", so that is what users actually type;
      Google normalises diacritics, but the literal match is bolded in the
      SERP and lifts CTR. Serbian copy keeps "Niš".
   2. /vega is NOT translated as "Vega brows" - that is a regional name for
      the technique that nobody searches abroad. EN targets "microblading" /
      "powder brows", DE targets "Microblading" / "Puderbrauen"; the Vega
      name survives in the description as our technique, not as the keyword.
   3. /baby-lips likewise: EN targets "lip blush", DE targets "Lippen
      Permanent Make-up". "Baby Lips" stays as the service name in the text.

   The primary DE audience is the Serbian diaspora from Germany, Austria and
   Switzerland travelling to Nis - hence German service keywords
   (Kosmetikstudio, Friseur, Wimpernverlängerung, Nagelstudio) rather than
   tourist-facing language.
   ───────────────────────────────────────────── */

import { SITE } from '../i18n/lang.mjs';
import { WIGS_ENABLED } from '../config/features.mjs';

/** Routes that exist in the codebase but are not public yet. */
const DISABLED_ROUTES = WIGS_ENABLED ? [] : ['/perike'];

/** Default social share image (stable public asset, not a hashed bundle). */
export const OG_IMAGE = `${SITE}/assets/img/logo/logo1.png`;

/** BeautySalon JSON-LD "description", translated per language. */
export const SALON_DESCRIPTION = {
  sr: 'Salon lepote u Nišu - trajna šminka, vega obrve, frizerske usluge, manikir i pedikir, nadogradnja trepavica, depilacija i edukacije.',
  en: 'Beauty salon in Nis, Serbia - permanent makeup, microblading, hair styling, manicure and pedicure, eyelash extensions, waxing and beauty courses.',
  de: 'Kosmetik- und Friseursalon in Nis, Serbien - Permanent Make-up, Microblading, Friseur, Maniküre und Pediküre, Wimpernverlängerung, Waxing und Kurse.',
};

const sr = {
  '/': {
    title: 'Salon lepote Niš | Vision Beauty - šminka, frizer, nokti',
    description: 'Vision Beauty - salon lepote u Nišu. Trajna šminka, vega obrve, frizer, manikir, trepavice i depilacija. Zakaži termin: 065/3585-545',
    h1: 'Salon lepote u Nišu - Vision Beauty',
    type: 'website',
  },
  '/permanent-makeup': {
    title: 'Trajna šminka Niš | Puder obrve, usne, ajlajner',
    description: 'Trajna šminka u Nišu - puder obrve, ajlajner i usne. Prirodan rezultat koji traje. Pogledaj radove i zakaži termin u Vision Beauty.',
    h1: 'Trajna šminka u Nišu',
    type: 'website',
  },
  '/vega': {
    title: 'Vega obrve Niš | Prirodne obrve - Vision Beauty',
    description: 'Vega obrve u Nišu - tehnika za prirodan i popunjen izgled obrva. Cene, radovi i zakazivanje u salonu Vision Beauty.',
    h1: 'Vega obrve u Nišu',
    type: 'website',
  },
  '/baby-lips': {
    title: 'Baby lips Niš | Trajna šminka usana',
    description: 'Baby lips tretman u Nišu - prirodna boja i definisan oblik usana. Vision Beauty salon lepote.',
    h1: 'Baby lips - trajna šminka usana u Nišu',
    type: 'website',
  },
  '/makeup': {
    title: 'Profesionalna šminka Niš | Svadbena i svečana',
    description: 'Profesionalno šminkanje u Nišu za venčanja, maturu i svečanosti. Ističemo prirodnu lepotu. Zakaži probu šminke.',
    h1: 'Profesionalna šminka u Nišu',
    type: 'website',
  },
  '/hairdresser': {
    // Deliberately re-targeted: the old title sat at position 3.8 on a weak
    // 1.5% CTR. "Ženski frizer" also catches that query without its own page.
    title: 'Frizerski salon Niš | Ženski frizer, farbanje, balayage',
    description: 'Ženski frizer u Nišu, šišanje, farbanje, balayage, feniranje i tretmani kose. Salon Vision Beauty, Miljkovačka 1. Zakaži: 065/3585-545',
    h1: 'Frizerski salon u Nišu',
    type: 'website',
  },
  /* Hair sub-pages. Logical key = the Serbian slug; EN/DE serve the same page
     on their own translated slug - see ROUTE_SLUGS in src/i18n/lang.mjs. */
  '/farbanje-kose': {
    title: 'Farbanje kose Niš | Cena, preliv, blajhanje',
    description: 'Farbanje kose u Nišu: cela kosa, izrastak, preliv, toniranje i blajhanje. Cene po dužini kose i savet oko nijanse. Vision Beauty, Niš.',
    h1: 'Farbanje kose u Nišu',
    type: 'website',
  },
  '/tretmani-kose': {
    title: 'Tretmani kose Niš | Olaplex, botoks, keratin',
    description: 'Tretmani za kosu u Nišu: Olaplex, botoks kose, matične ćelije i Quick Repair. Šta koji tretman rešava i koliko traje efekat. Vision Beauty.',
    h1: 'Tretmani za kosu u Nišu',
    type: 'website',
  },
  '/musko-sisanje': {
    title: 'Muški frizer Niš | Šišanje i fade',
    description: 'Muški frizer u Nišu: klasično šišanje i fade, uz preciznu obradu kontura. Cene od 700 din. Vision Beauty, Miljkovačka 1.',
    h1: 'Muški frizer u Nišu',
    type: 'website',
  },
  '/ekstenzije-za-kosu': {
    title: 'Clip in ekstenzije Niš | Rep i umeci za kosu',
    description: 'Clip-in ekstenzije, repovi i umeci za kosu u Nišu. Ljudska i sintetička kosa, dužine 50 do 70 cm. Dođi na besplatnu konsultaciju.',
    h1: 'Ekstenzije za kosu u Nišu',
    type: 'website',
  },
  '/perike': {
    title: 'Perike Niš | Ljudska i sintetička kosa',
    description: 'Perike u Nišu: ljudska i sintetička kosa, dužine 60 do 70 cm. Nijansu biramo zajedno u salonu. Besplatna konsultacija bez obaveze.',
    h1: 'Perike u Nišu',
    type: 'website',
  },
  '/nails': {
    title: 'Manikir i pedikir Niš | Gel lak i nadogradnja noktiju',
    description: 'Manikir, pedikir, gel lak i nadogradnja noktiju u Nišu. SPA tretman ruku, estetski pedikir. Zakaži termin: 065/3585-545',
    h1: 'Manikir i pedikir u Nišu',
    type: 'website',
  },
  '/eyelashes': {
    title: 'Nadogradnja trepavica Niš | Volumen i lash lift',
    description: 'Nadogradnja trepavica u Nišu - klasika, volumen i lash lift. Prirodan izgled bez maskare. Vision Beauty, Niš.',
    h1: 'Nadogradnja trepavica u Nišu',
    type: 'website',
  },
  '/depilation': {
    title: 'Depilacija Niš | Vosak i šećerna pasta',
    description: 'Depilacija u Nišu - voskom i šećernom pastom. Nežno, efikasno i dugotrajno glatka koža. Zakaži termin.',
    h1: 'Depilacija u Nišu',
    type: 'website',
  },
  /* /ponude - the running promotions page. The offer itself (date, seats,
     price) is configured in src/config/offers.mjs; only the copy lives here.
     Rewrite the description whenever the headline offer changes. */
  '/ponude': {
    title: 'Akcije i ponude | Salon lepote Vision Beauty Niš',
    description: 'Aktuelne ponude salona Vision Beauty u Nišu. Septembarska grupa kursa šminkanja, napredni kurs i pogodnosti za kosu. Zakaži: 065/3585-545',
    h1: 'Aktuelne ponude',
    type: 'website',
  },
  '/educations': {
    title: 'Kurs šminkanja Niš | Edukacije za šminkere',
    description: 'Kursevi šminkanja i edukacije u Nišu - od osnova do profesionalnog nivoa. Mala grupa, sertifikat, praksa.',
    h1: 'Kursevi šminkanja i edukacije u Nišu',
    type: 'website',
  },
  /* Logical key = the Serbian slug. EN/DE serve the same page on their own
     translated slug - see ROUTE_SLUGS in src/i18n/lang.mjs. */
  '/napredni-kurs-sminkanja': {
    title: 'Napredni kurs šminkanja Niš | Za profesionalce',
    description: 'Individualni kurs za šminkere koje već rade - tehnika, cene, snimanje tutorijala i pokretanje sopstvenog salona. Vision Beauty, Niš.',
    h1: 'Napredni kurs šminkanja u Nišu',
    type: 'website',
  },
  '/giftcard': {
    title: 'Poklon vaučer Niš | Giftcard salona Vision Beauty',
    description: 'Poklon vaučer za usluge salona lepote u Nišu. Idealan poklon za rođendan, godišnjicu ili praznike. Vision Beauty, Niš.',
    h1: 'Poklon vaučeri salona Vision Beauty',
    type: 'website',
  },
  '/service': {
    title: 'Usluge salona lepote u Nišu | Vision Beauty',
    description: 'Sve usluge na jednom mestu: trajna šminka, frizer, nokti, trepavice, depilacija i edukacije. Vision Beauty, Niš.',
    h1: 'Usluge salona lepote u Nišu',
    type: 'website',
  },
  '/pricing': {
    title: 'Cenovnik - Salon lepote Vision Beauty Niš',
    description: 'Cene svih usluga: trajna šminka, frizer, manikir, trepavice, depilacija. Ažuriran cenovnik salona Vision Beauty u Nišu.',
    h1: 'Cenovnik salona Vision Beauty Niš',
    type: 'website',
  },
  '/gallery': {
    title: 'Galerija radova | Vision Beauty Niš',
    description: 'Pogledaj naše radove - trajna šminka, frizure, nokti i trepavice. Pre i posle, iz salona Vision Beauty u Nišu.',
    h1: 'Galerija radova - Vision Beauty Niš',
    type: 'website',
  },
  '/blog': {
    title: 'Blog o lepoti i negi | Vision Beauty Niš',
    description: 'Saveti o nezi kose, noktiju i kože, trendovi u šminkanju i odgovori na česta pitanja. Vision Beauty, Niš.',
    h1: 'Blog o lepoti i negi',
    type: 'website',
  },
  '/balayage': {
    // Converted from a blog post into a service page: same URL, new intent,
    // so og:type moves from 'article' to 'website' and BlogPosting gives way
    // to Service in head.mjs.
    title: 'Balayage Niš | Cena, air touch i pramenovi',
    description: 'Balayage, air touch i pramenovi u Nišu. Cene, trajanje, izgled i nega posle tretmana. Pogledaj radove i zakaži termin u salonu Vision Beauty.',
    h1: 'Balayage u Nišu',
    type: 'website',
  },
  '/about': {
    title: 'O nama | Vision Beauty salon lepote Niš',
    description: '10 godina iskustva u svetu lepote. Upoznaj tim i priču salona Vision Beauty u Nišu.',
    h1: 'O nama - Vision Beauty Niš',
    type: 'website',
  },
  '/contact': {
    title: 'Kontakt | Vision Beauty - salon lepote u Nišu',
    description: 'Adresa, telefon i radno vreme salona Vision Beauty u Nišu. Miljkovačka 1. Zakaži termin: 065/3585-545',
    h1: 'Kontakt - Vision Beauty Niš',
    type: 'website',
  },
  '/jobs': {
    title: 'Posao | Vision Beauty salon lepote Niš',
    description: 'Otvorene pozicije u salonu Vision Beauty u Nišu. Pridruži se timu.',
    h1: 'Posao u salonu Vision Beauty Niš',
    type: 'website',
  },
};

/* English copy. City spelled "Nis" on purpose - see the header note. */
const en = {
  '/': {
    title: 'Beauty Salon in Nis | Vision Beauty - Hair, Nails, Makeup',
    description: 'Vision Beauty - full-service beauty salon in Nis, Serbia. Permanent makeup, hair styling, manicure, lash extensions and waxing. Book: +381 65 3585 545',
    h1: 'Beauty Salon in Nis - Vision Beauty',
  },
  '/permanent-makeup': {
    title: 'Permanent Makeup Nis | Powder Brows, Lips, Eyeliner',
    description: 'Permanent makeup in Nis - powder brows, lip blush and eyeliner. Natural, long-lasting results. See our work and book a consultation at Vision Beauty.',
    h1: 'Permanent Makeup in Nis',
  },
  '/vega': {
    title: 'Microblading & Powder Brows in Nis | Vision Beauty',
    description: 'Natural-looking brows in Nis with our Vega technique - microblading and powder brows for fuller, well-shaped eyebrows. Free consultation beforehand.',
    h1: 'Microblading and Powder Brows in Nis',
  },
  '/baby-lips': {
    title: 'Lip Blush Nis | Baby Lips Permanent Lip Makeup',
    description: 'Baby Lips lip blush in Nis - soft, natural colour and a defined lip shape that lasts. No lipstick needed. Vision Beauty salon, Nis.',
    h1: 'Baby Lips - Lip Blush in Nis',
  },
  '/makeup': {
    title: 'Makeup Artist Nis | Bridal & Special Occasion Makeup',
    description: 'Professional makeup in Nis for weddings, proms and special occasions. Long-lasting and photo-ready. Trial session available. Book at Vision Beauty.',
    h1: 'Professional Makeup in Nis',
  },
  '/hairdresser': {
    title: "Hair Salon Nis | Women's Hairdresser, Colour, Balayage",
    description: "Women's hairdresser in Nis: cutting, colouring, balayage, blow-dry and hair treatments. Vision Beauty salon, Miljkovacka 1. Book: 065/3585-545",
    h1: 'Hair Salon in Nis',
  },
  /* EN/DE copy below is a first pass, pending the owner's own wording -
     the same worksheet flow used for the course pages. */
  '/farbanje-kose': {
    title: 'Hair Colouring Nis | Prices, Toner, Bleaching',
    description: 'Hair colouring in Nis: full colour, roots, toner and bleaching. Prices by hair length and honest advice on the shade. Vision Beauty, Nis.',
    h1: 'Hair Colouring in Nis',
  },
  '/tretmani-kose': {
    title: 'Hair Treatments Nis | Olaplex, Botox, Keratin',
    description: 'Hair treatments in Nis: Olaplex, hair botox, stem cells and Quick Repair. What each one fixes and how long the effect lasts. Vision Beauty.',
    h1: 'Hair Treatments in Nis',
  },
  '/musko-sisanje': {
    title: "Men's Barber Nis | Haircuts and Fades",
    description: "Men's barber in Nis: classic cuts and fades with a precise line-up. From 700 RSD. Vision Beauty, Miljkovacka 1, Nis.",
    h1: "Men's Haircuts in Nis",
  },
  '/ekstenzije-za-kosu': {
    title: 'Clip-in Hair Extensions Nis | Ponytails and Wefts',
    description: 'Clip-in hair extensions, ponytails and wefts in Nis. Human and synthetic hair, 50 to 70 cm. Come in for a free consultation.',
    h1: 'Clip-in Hair Extensions in Nis',
  },
  '/perike': {
    title: 'Wigs in Nis | Human Hair and Synthetic',
    description: 'Wigs in Nis: human and synthetic hair, 60 to 70 cm. We pick the shade together in the salon. Free consultation, no obligation.',
    h1: 'Wigs in Nis',
  },
  '/nails': {
    title: 'Nail Salon Nis | Manicure, Pedicure & Gel Nails',
    description: 'Manicure, pedicure, gel polish and nail extensions in Nis. Spa hand treatment and aesthetic pedicure. Book your appointment: +381 65 3585 545',
    h1: 'Manicure and Pedicure in Nis',
  },
  '/eyelashes': {
    title: 'Eyelash Extensions Nis | Volume, Classic & Lash Lift',
    description: 'Eyelash extensions in Nis - classic 1:1, volume sets and lash lift. Wake up ready, no mascara needed. Vision Beauty salon, Nis, Serbia.',
    h1: 'Eyelash Extensions in Nis',
  },
  '/depilation': {
    title: 'Waxing & Sugaring in Nis | Hair Removal',
    description: 'Hair removal in Nis with warm wax and sugaring paste. Gentle, effective and long-lasting smooth skin, including sensitive areas. Book today.',
    h1: 'Waxing and Sugaring in Nis',
  },
  '/ponude': {
    title: 'Offers & Deals | Vision Beauty Salon Nis',
    description: 'Current offers at Vision Beauty salon in Nis. The September makeup course group, the advanced course and deals on hair. Book: 065/3585-545',
    h1: 'Current Offers',
  },
  '/educations': {
    title: 'Makeup Courses in Nis | Beauty Training',
    description: 'Makeup and beauty courses in Nis - from beginner to professional level. Small groups, hands-on practice on models and a certificate on completion.',
    h1: 'Makeup Courses and Beauty Training in Nis',
  },
  '/napredni-kurs-sminkanja': {
    title: 'Advanced Makeup Course Nis | For Working Artists',
    description: 'One-on-one course for working makeup artists in Nis - advanced technique, pricing, filming and opening your own salon.',
    h1: 'Advanced Makeup Course in Nis',
  },
  '/giftcard': {
    title: 'Beauty Gift Card Nis | Vision Beauty Salon Voucher',
    description: 'Gift card for beauty treatments in Nis - hair, nails, makeup or lashes. A ready-made gift for birthdays, anniversaries and holidays.',
    h1: 'Vision Beauty Gift Cards',
  },
  '/service': {
    title: 'Our Services | Beauty Salon Vision Beauty, Nis',
    description: 'All services in one place: permanent makeup, hair, nails, eyelash extensions, waxing and beauty courses. Vision Beauty salon in Nis, Serbia.',
    h1: 'Our Services in Nis',
  },
  '/pricing': {
    title: 'Price List | Vision Beauty Salon Nis',
    description: 'Current prices for permanent makeup, hair styling, manicure, eyelash extensions and waxing at Vision Beauty salon in Nis.',
    h1: 'Vision Beauty Price List, Nis',
  },
  '/gallery': {
    title: 'Gallery | Vision Beauty Salon Nis - Our Work',
    description: 'See our work - permanent makeup, hairstyles, nails and eyelash extensions. Before and after photos from Vision Beauty salon in Nis.',
    h1: 'Our Work - Vision Beauty Nis',
  },
  '/blog': {
    title: 'Beauty Blog | Tips & Trends - Vision Beauty Nis',
    description: 'Hair, nail and skin care advice, makeup trends and answers to common questions from our beauty salon in Nis.',
    h1: 'Beauty and Care Blog',
  },
  '/balayage': {
    title: 'Balayage Nis | Price, Air Touch and Highlights',
    description: 'Balayage, air touch and highlights in Nis. Prices, how long it takes, how long it lasts and aftercare. See our work and book at Vision Beauty.',
    h1: 'Balayage in Nis',
  },
  '/about': {
    title: 'About Us | Vision Beauty Beauty Salon in Nis',
    description: '10 years of experience in the beauty industry. Meet the team and the story behind Vision Beauty salon in Nis, Serbia.',
    h1: 'About Vision Beauty, Nis',
  },
  '/contact': {
    title: 'Contact | Vision Beauty Beauty Salon in Nis',
    description: 'Address, phone and opening hours of Vision Beauty salon in Nis. Miljkovacka 1. Book your appointment: +381 65 3585 545',
    h1: 'Contact Vision Beauty, Nis',
  },
  '/jobs': {
    title: 'Careers | Vision Beauty Salon in Nis',
    description: 'Open positions at Vision Beauty salon in Nis. Join our team of beauty professionals.',
    h1: 'Careers at Vision Beauty Nis',
  },
};

/* German copy, aimed at the Serbian diaspora in DE/AT/CH - see header note. */
const de = {
  '/': {
    title: 'Kosmetikstudio Nis | Vision Beauty - Haare, Nägel, Make-up',
    description: 'Vision Beauty - Kosmetik- und Friseursalon in Nis, Serbien. Permanent Make-up, Friseur, Maniküre, Wimpernverlängerung und Waxing. Termin: +381 65 3585 545',
    h1: 'Kosmetiksalon in Nis - Vision Beauty',
  },
  '/permanent-makeup': {
    title: 'Permanent Make-up Nis | Puderbrauen, Lippen, Lidstrich',
    description: 'Permanent Make-up in Nis - Puderbrauen, Lippen und Lidstrich. Natürliches Ergebnis, das lange hält. Ergebnisse ansehen und Termin bei Vision Beauty buchen.',
    h1: 'Permanent Make-up in Nis',
  },
  '/vega': {
    title: 'Microblading & Puderbrauen in Nis | Vision Beauty',
    description: 'Natürlich volle Augenbrauen in Nis - Microblading und Puderbrauen mit unserer Vega-Technik. Kostenlose Beratung vor der Behandlung.',
    h1: 'Microblading und Puderbrauen in Nis',
  },
  '/baby-lips': {
    title: 'Lippen Permanent Make-up Nis | Baby Lips',
    description: 'Baby Lips in Nis - natürliche Farbe und klar definierte Lippenkontur, die lange hält. Ohne täglichen Lippenstift. Vision Beauty, Nis.',
    h1: 'Baby Lips - Lippen Permanent Make-up in Nis',
  },
  '/makeup': {
    title: 'Make-up Artist Nis | Braut- und Abend-Make-up',
    description: 'Professionelles Make-up in Nis für Hochzeit, Abschlussball und besondere Anlässe. Langanhaltend und fotogen. Mit Probetermin. Jetzt buchen.',
    h1: 'Professionelles Make-up in Nis',
  },
  '/hairdresser': {
    title: 'Friseur Nis | Damenfriseur, Farbe, Balayage',
    description: 'Damenfriseur in Nis: Haarschnitt, Färben, Balayage, Föhnen und Haarbehandlungen. Salon Vision Beauty, Miljkovacka 1. Termin: 065/3585-545',
    h1: 'Friseursalon in Nis',
  },
  '/farbanje-kose': {
    title: 'Haare Färben Nis | Preise, Tönung, Blondierung',
    description: 'Haare färben in Nis: Komplettfärbung, Ansatz, Tönung und Blondierung. Preise nach Haarlänge und ehrliche Beratung zum Farbton. Vision Beauty, Nis.',
    h1: 'Haare Färben in Nis',
  },
  '/tretmani-kose': {
    title: 'Haarbehandlungen Nis | Olaplex, Botox, Keratin',
    description: 'Haarbehandlungen in Nis: Olaplex, Haar-Botox, Stammzellen und Quick Repair. Was jede Behandlung bewirkt und wie lange der Effekt hält.',
    h1: 'Haarbehandlungen in Nis',
  },
  '/musko-sisanje': {
    title: 'Herrenfriseur Nis | Haarschnitt und Fade',
    description: 'Herrenfriseur in Nis: klassischer Haarschnitt und Fade mit präziser Konturenarbeit. Ab 700 RSD. Vision Beauty, Miljkovacka 1, Nis.',
    h1: 'Herrenhaarschnitt in Nis',
  },
  '/ekstenzije-za-kosu': {
    title: 'Clip-in Extensions Nis | Pferdeschwanz und Haarteile',
    description: 'Clip-in Extensions, Pferdeschwänze und Haarteile in Nis. Echthaar und Kunsthaar, 50 bis 70 cm. Kostenlose Beratung im Salon.',
    h1: 'Clip-in Extensions in Nis',
  },
  '/perike': {
    title: 'Perücken Nis | Echthaar und Kunsthaar',
    description: 'Perücken in Nis: Echthaar und Kunsthaar, 60 bis 70 cm. Den Farbton wählen wir gemeinsam im Salon. Kostenlose Beratung ohne Verpflichtung.',
    h1: 'Perücken in Nis',
  },
  '/nails': {
    title: 'Nagelstudio Nis | Maniküre, Pediküre & Gelnägel',
    description: 'Maniküre, Pediküre, Gellack und Nagelmodellage in Nis. Spa-Handpflege und ästhetische Fußpflege. Termin buchen: +381 65 3585 545',
    h1: 'Maniküre und Pediküre in Nis',
  },
  '/eyelashes': {
    title: 'Wimpernverlängerung Nis | Volumen & Wimpernlifting',
    description: 'Wimpernverlängerung in Nis - klassisch 1:1, Volumen und Wimpernlifting. Wacher Blick ganz ohne Mascara. Vision Beauty Salon in Nis.',
    h1: 'Wimpernverlängerung in Nis',
  },
  '/depilation': {
    title: 'Waxing & Sugaring in Nis | Haarentfernung',
    description: 'Haarentfernung in Nis mit warmem Wachs und Zuckerpaste. Sanft, gründlich und langanhaltend glatte Haut, auch an empfindlichen Stellen.',
    h1: 'Haarentfernung in Nis',
  },
  '/ponude': {
    title: 'Angebote & Aktionen | Vision Beauty Salon Nis',
    description: 'Aktuelle Angebote im Salon Vision Beauty in Nis. Die September-Kursgruppe, der Aufbaukurs und Aktionen für Haare. Termin: 065/3585-545',
    h1: 'Aktuelle Angebote',
  },
  '/educations': {
    title: 'Make-up Kurse in Nis | Ausbildung bei Vision Beauty',
    description: 'Make-up- und Beauty-Kurse in Nis - vom Einsteiger bis zum Profi. Kleine Gruppen, Praxis am Modell und Zertifikat zum Abschluss.',
    h1: 'Make-up Kurse und Ausbildung in Nis',
  },
  '/napredni-kurs-sminkanja': {
    title: 'Make-up Aufbaukurs Nis | Für Profis',
    description: 'Einzelcoaching für Make-up Artists mit Erfahrung in Nis - Technik, Preise, Filmen und der Weg zum eigenen Salon.',
    h1: 'Make-up Aufbaukurs in Nis',
  },
  '/giftcard': {
    title: 'Beauty Gutschein Nis | Vision Beauty Salon',
    description: 'Gutschein für Beauty-Behandlungen in Nis - Haare, Nägel, Make-up oder Wimpern. Das passende Geschenk für Geburtstag, Jubiläum und Feiertage.',
    h1: 'Gutscheine von Vision Beauty',
  },
  '/service': {
    title: 'Leistungen | Kosmetiksalon Vision Beauty in Nis',
    description: 'Alle Leistungen auf einen Blick: Permanent Make-up, Friseur, Nägel, Wimpernverlängerung, Waxing und Kurse. Vision Beauty in Nis, Serbien.',
    h1: 'Unsere Leistungen in Nis',
  },
  '/pricing': {
    title: 'Preise | Vision Beauty Kosmetiksalon in Nis',
    description: 'Aktuelle Preise für Permanent Make-up, Friseur, Maniküre, Wimpernverlängerung und Waxing im Salon Vision Beauty in Nis.',
    h1: 'Preisliste Vision Beauty, Nis',
  },
  '/gallery': {
    title: 'Galerie | Vision Beauty Salon Nis - Unsere Arbeiten',
    description: 'Unsere Arbeiten ansehen - Permanent Make-up, Frisuren, Nägel und Wimpern. Vorher-Nachher-Bilder aus dem Salon Vision Beauty in Nis.',
    h1: 'Unsere Arbeiten - Vision Beauty Nis',
  },
  '/blog': {
    title: 'Beauty Blog | Tipps und Trends - Vision Beauty Nis',
    description: 'Tipps zu Haar-, Nagel- und Hautpflege, Make-up-Trends und Antworten auf häufige Fragen aus unserem Salon in Nis.',
    h1: 'Blog rund um Beauty und Pflege',
  },
  '/balayage': {
    title: 'Balayage Nis | Preis, Air Touch und Strähnen',
    description: 'Balayage, Air Touch und Strähnen in Nis. Preise, Dauer, Haltbarkeit und Pflege danach. Ergebnisse ansehen und Termin bei Vision Beauty buchen.',
    h1: 'Balayage in Nis',
  },
  '/about': {
    title: 'Über uns | Vision Beauty Kosmetiksalon in Nis',
    description: '10 Jahre Erfahrung in der Beauty-Branche. Lernen Sie das Team und die Geschichte des Salons Vision Beauty in Nis kennen.',
    h1: 'Über Vision Beauty in Nis',
  },
  '/contact': {
    title: 'Kontakt | Vision Beauty Kosmetiksalon in Nis',
    description: 'Adresse, Telefon und Öffnungszeiten des Salons Vision Beauty in Nis. Miljkovacka 1. Termin buchen: +381 65 3585 545',
    h1: 'Kontakt - Vision Beauty in Nis',
  },
  '/jobs': {
    title: 'Jobs | Vision Beauty Kosmetiksalon in Nis',
    description: 'Offene Stellen im Salon Vision Beauty in Nis. Werden Sie Teil unseres Teams.',
    h1: 'Karriere bei Vision Beauty in Nis',
  },
};

/**
 * routeMeta[route] = { sr: {...}, en: {...}, de: {...} }
 * Serbian drives the route list; en/de entries are looked up by the same key.
 */
export const routeMeta = Object.fromEntries(
  Object.entries(sr).map(([route, data]) => [
    route,
    { sr: data, en: en[route] || {}, de: de[route] || {} },
  ])
);

/**
 * All logical routes that receive full SEO treatment (drives the prerender
 * and the sitemap). Routes switched off in src/config/features.mjs are left
 * out, so a page that is not public gets no prerendered HTML and no meta.
 */
export const SEO_ROUTES = Object.keys(sr).filter((route) => !DISABLED_ROUTES.includes(route));

/**
 * Resolve meta for a route+lang, field by field, falling back to Serbian
 * for any missing localized value. Unknown routes fall back to the homepage.
 */
export function getMeta(route, lang) {
  const entry = routeMeta[route] || routeMeta['/'];
  const base = entry.sr;
  const loc = entry[lang] || {};
  return {
    title: loc.title || base.title,
    description: loc.description || base.description,
    h1: loc.h1 || base.h1,
    type: base.type, // og:type is language-independent
  };
}

/** Salon JSON-LD description with sr fallback. */
export function salonDescription(lang) {
  return SALON_DESCRIPTION[lang] || SALON_DESCRIPTION.sr;
}

/*
  When adding a route: add it to `sr` first (it drives SEO_ROUTES and the
  sitemap), then add the matching key to `en` and `de`. A missing en/de key
  is not a build error - getMeta() silently serves Serbian copy on that
  language's page - so check all three objects have the same keys.
*/
